/* eslint-disable @next/next/no-img-element */
import { gSP } from "app/blitz-server"
import { Main } from "app/core/components/layout/Main"
import Footer from "app/splash/components/Footer"
import Header from "app/splash/components/Header"
import Head from "next/head"
import { STag } from "@superwall-me/sui"
import moment from "moment"
import _ from "lodash"
import { HoverSkew } from "app/splash/components/HoverSkew"
import Link from "next/link"
import contentful, { Post } from "integrations/contentful"
import BlogLayout from "app/core/layouts/Blog"
import { Templates } from "app/splash/components/splash-sections/Templates"
import { Customers } from "app/splash/components/splash-sections/Customers"
import { Section, Spacer, Subtitle, Title } from "app/splash/components/misc"
import { SignUpButton } from "app/splash/components/Main"
import { Reviews } from "app/splash/components/splash-sections/Reviews"
import { ColorBlur } from "app/splash/components/ColorBlur"
import { useSession } from "@blitzjs/auth"

type Props = {
  allPosts: Post[]
  raw: any
}

export const PostAuthor = ({ post }: { post: Post }) => {
  return (
    (<div className="relative mt-4 flex items-center ">
      <div className=" flex flex-row justify-center items-center">
        {post.author && (
          // eslint-disable-next-line jsx-a11y/alt-text
          (<img src={post.author?.image.url} className="w-10 h-10 rounded-xl mr-4 shadow-xl" />)
        )}
      </div>
      <div className="">
        <p className="font-semibold -mb-1">{post.author?.name}</p>

        <time dateTime={post.createdAt.toString()} className=" text-grayscale-lightest text-sm">
          {moment(post.createdAt).format("MMMM D, YYYY")}
        </time>
      </div>
    </div>)
  );
}

export const FeaturedPost = ({ post }: { post: Post }) => {
  const subtitle = _.truncate(post.subtitle, {
    length: 90,
    separator: " ",
  })

  return (
    (<article className="px-1 md:px-10">
      <HoverSkew glowStength="light" clickable={true} borderRadius="16px" className="p-3 my-10">
        <Link href={`/blog/${post.slug}`}>

          <div className="md:grid md:grid-cols-[2fr,3fr] lg:grid-cols-[1fr,1fr] gap-7 md:pr-3 lg:pr-5">
            {post.image && (
              <div className="flex flex-col items-center justify-center">
                <img
                  className="rounded-lg ring-1 ring-white/10 shadow-xl"
                  src={post.image.url}
                  alt={post.image.title}
                />
              </div>
            )}
            <div
              className={`${
                post.image ? "" : "col-span-2 ml-4"
              } flex flex-col items-start justify-between gap-2 lg:my-2 px-2 pt-2 md:p-0`}
            >
              <div className="space-y-3 ">
                <div className="my-2">
                  <STag variant="primary">
                    <span className="text-base">{post.tags[0] || "Latest"}</span>
                  </STag>
                </div>
                <h2 className="text-2xl xl:text-3xl font-medium ">{post.title}</h2>
                <h3 className="font-light text-grayscale-lightest line-clamp-2 text-lg xl:text-xl ">
                  {subtitle}
                </h3>
              </div>

              <PostAuthor post={post} />
            </div>
          </div>

        </Link>
      </HoverSkew>
    </article>)
  );
}

export const BlogPost = ({ post }: { post: Post }) => {
  return (
    (<HoverSkew
      key={post.slug}
      glowStength="extra-light"
      clickable={true}
      borderRadius="10px"
      className={` flex-shrink hover:border-white/20 transition self-shrink flex flex-row items-stretch`}
    >
      <div className="flex flex-col items-start justify-between  p-5 pb-4 md:p-5">
        <Link href={`/blog/${post.slug}`} className="">

          <article className="flex flex-col items-start justify-between">
            <div className="flex items-center gap-x-4 text-sm">
              {post.tags[0] && <STag variant="primary">{post.tags[0]}</STag>}
            </div>
            <div className="group relative">
              <h3 className="mt-3 text-2xl font-semibold">{post.title}</h3>
              <p className="mt-3 line-clamp-3 text-lg font-light text-grayscale-lightest">
                {_.truncate(post.subtitle, {
                  length: 90,
                  separator: " ",
                })}
              </p>
            </div>
          </article>

        </Link>
        <Link href={`/blog/${post.slug}`} className="my-0">

          <PostAuthor post={post} />

        </Link>
      </div>
    </HoverSkew>)
  );
}

const Blog = ({ allPosts }: Props) => {
  const firstPost = allPosts[0]
  const posts = allPosts.slice(1)

  const session = useSession({ suspense: false })

  return (
    <>
      <Header />

      <Main>
        <Head>
          <title>Blog — Superwall</title>
        </Head>
        <div className="h-1 md:h-14"></div>
        <div className="max-w-7xl mx-auto mb-10">
          {firstPost && <FeaturedPost post={firstPost} />}
        </div>
        <div className="relative">
          <p className="absolute left-[50%] transform translate-x-[-50%] translate-y-[-50%] uppercase text-sm tracking-widest text-white/30 bg-grayscale-darkest p-1 px-2 rounded-xl">
            All Posts
          </p>
          <div
            className={`h-[1px] w-[100%] my-6 md:my-14 } bg-gradient-to-r from-sky-300/0 via-purple-200/20 to-sky-300/0`}
          ></div>
        </div>

        <div className="max-w-7xl px-1 md:px-10 grid grid-cols-1 md:grid-cols-2 mx-auto gap-3 md:gap-5 my-10">
          {posts.map((post, index) => {
            return (
              <BlogPost key={`${post.createdAt.toString()}-${post.title}-${index}`} post={post} />
            )
          })}
        </div>

        {!session.userId && (
          <div className="mt-20 md:-mb-28 px-1">
            <Reviews title="So, what are you waiting for?">
              <div className="flex flex-row items-center gap-10 mb-16">
                <SignUpButton
                  source="main-cta"
                  mainCta="Start for free"
                  bookDemoCta={"Talk to sales"}
                  showBookDemo={true}
                ></SignUpButton>
              </div>
            </Reviews>
          </div>
        )}
      </Main>
      <Footer />
    </>
  )
}

Blog.getLayout = (page) => <BlogLayout>{page}</BlogLayout>

export default Blog

export const getStaticProps = gSP(async () => {
  const posts = await contentful.getAllPosts()

  return {
    props: {
      allPosts: posts,
    },
    revalidate: 10,
  }
})
